/**
 * PrimaryButton Component
 * Standardized primary action button
 */

import React, { type ReactNode, type MouseEventHandler } from 'react';
import { designTokens } from '../../constants/designTokens';

interface PrimaryButtonProps {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'danger';
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
}

function PrimaryButtonComponent({
  children,
  onClick,
  disabled = false,
  loading = false,
  fullWidth = false,
  size = 'md',
  variant = 'primary',
  className = '',
  type = 'button',
  style,
}: PrimaryButtonProps) {
  const buttonClass = `
    primary-button 
    primary-button-${size} 
    primary-button-${variant}
    ${fullWidth ? 'primary-button-full' : ''}
    ${loading ? 'primary-button-loading' : ''}
    ${className}
  `.trim();

  return (
    <>
      <button
        type={type}
        className={buttonClass}
        onClick={onClick}
        disabled={disabled || loading}
        style={style}
      >
        {loading ? (
          <span className="button-loading-content">
            <span className="button-spinner" />
            Processing...
          </span>
        ) : (
          children
        )}
      </button>

      <style>{`
        .primary-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: ${designTokens.typography.fontFamily.primary};
          font-weight: ${designTokens.components.button.fontWeight};
          border: none;
          border-radius: ${designTokens.components.button.borderRadius};
          cursor: pointer;
          transition: ${designTokens.components.button.transition};
          outline: none;
          position: relative;
          
          /* Standardized button height */
          min-height: ${designTokens.components.button.height};
          min-width: 44px;
          
          /* Touch device optimizations */
          -webkit-tap-highlight-color: transparent;
          user-select: none;
          touch-action: manipulation;
        }
        
        .primary-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        /* Base button sizes - Standardized 422×70 */
        .primary-button-sm {
          padding: ${designTokens.spacing.sm} ${designTokens.spacing.md};
          font-size: ${designTokens.typography.fontSize.sm};
          min-height: ${designTokens.components.button.height};
        }

        .primary-button-md {
          padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
          font-size: ${designTokens.typography.fontSize.base};
          min-height: ${designTokens.components.button.height};
          font-weight: ${designTokens.typography.fontWeight.semibold};
        }

        .primary-button-lg {
          padding: ${designTokens.spacing.lg} ${designTokens.spacing.xl};
          font-size: ${designTokens.typography.fontSize.lg};
          min-height: ${designTokens.components.button.height};
          font-weight: ${designTokens.typography.fontWeight.semibold};
        }
        
        /* Mobile (up to 767px) - Touch-optimized */
        ${designTokens.media.mobile} {
          .primary-button:not(.primary-button-full) {
            width: ${designTokens.components.button.width.mobile}; /* As requested - only for non-full-width buttons */
            margin: 0 auto;
          }
          
          .primary-button-sm,
          .primary-button-md,
          .primary-button-lg {
            min-height: ${designTokens.components.button.height};
          }

          /* Override for full width buttons on mobile */
          .primary-button-full {
            width: 100%;
          }
        }
        
        /* Color variants */
        .primary-button-primary {
          background-color: ${designTokens.colors.brand.primary};
          color: ${designTokens.colors.text.buttonPrimary};
        }
        
        .primary-button-primary:hover:not(:disabled) {
          background-color: ${designTokens.colors.brand.primaryHover};
        }
        
        .primary-button-primary:active:not(:disabled) {
          background-color: ${designTokens.colors.brand.primaryActive};
        }
        
        .primary-button-secondary {
          background-color: ${designTokens.colors.background.secondary};
          color: ${designTokens.colors.text.primary};
          border: 1px solid ${designTokens.colors.border.default};
        }
        
        .primary-button-secondary:hover:not(:disabled) {
          background-color: ${designTokens.colors.background.tertiary};
          border-color: ${designTokens.colors.border.light};
        }
        
        .primary-button-danger {
          background-color: ${designTokens.colors.semantic.error};
          color: ${designTokens.colors.text.primary};
        }
        
        .primary-button-danger:hover:not(:disabled) {
          background-color: ${designTokens.colors.red.bg};
        }
        
        /* Full width variant */
        .primary-button-full {
          width: 100%;
        }
        
        /* Tablet (768px-1023px) - Smooth progression */
        ${designTokens.media.tablet} {
          .primary-button:not(.primary-button-full) {
            width: ${designTokens.components.button.width.tablet}; /* Fixed width for consistency */
            margin: 0 auto;
          }
          
          .primary-button-sm,
          .primary-button-md,
          .primary-button-lg {
            min-height: ${designTokens.components.button.height};
          }
        }

        /* Desktop (1024px+) */
        ${designTokens.media.desktop} {
          .primary-button:not(.primary-button-full) {
            width: ${designTokens.components.button.width.desktop};
            margin: 0 auto;
          }

          .primary-button-sm,
          .primary-button-md,
          .primary-button-lg {
            min-height: ${designTokens.components.button.height};
          }
        }
        
        /* Loading state */
        .primary-button-loading {
          color: transparent;
        }
        
        .button-loading-content {
          display: flex;
          align-items: center;
          gap: ${designTokens.spacing.sm};
          color: ${designTokens.colors.text.primary};
        }
        
        .button-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid ${designTokens.colors.text.primary};
          border-top-color: transparent;
          border-radius: 50%;
          animation: button-spin 0.8s linear infinite;
        }
        
        @keyframes button-spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        /* Focus styles */
        .primary-button:focus-visible {
          box-shadow: 0 0 0 3px ${designTokens.colors.withOpacity.brandFocus};
        }
      `}</style>
    </>
  );
}

// Custom comparison function that properly compares all props including onClick
function arePropsEqual(
  prevProps: PrimaryButtonProps,
  nextProps: PrimaryButtonProps
): boolean {
  return (
    prevProps.children === nextProps.children &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.loading === nextProps.loading &&
    prevProps.fullWidth === nextProps.fullWidth &&
    prevProps.size === nextProps.size &&
    prevProps.variant === nextProps.variant &&
    prevProps.className === nextProps.className &&
    prevProps.type === nextProps.type &&
    prevProps.onClick === nextProps.onClick // Now comparing onClick to ensure button updates
  );
}

export const PrimaryButton = React.memo(PrimaryButtonComponent, arePropsEqual);
